<script>
    import {
        Button,
        Col,
        Container,
        MaterialApp,
        Overlay,
        ProgressCircular,
        Row,
        Select,
        Divider
    } from "svelte-materialify/src";

    import * as fkde from "fast-kde";
    import { onMount } from "svelte";
    import * as d3 from "d3";
    import legend from "d3-svg-legend";
    import textures from "textures";
    import * as druid from "@saehrimnir/druidjs/dist/druid.js";

    const datasets = [
        "1_coder_seen_issue_picture_count.csv",
        "50_percent_coders_seen_issue.csv",
        "amount%20of%20average%20labels%20per%20image.csv",
        "average_labels.csv",
        "issues_years.csv",
        "labels_over_years.csv",
    ];
    const issues_names = [
        "resolution_too_low",
        "shapes_too_thinsmall",
        "issues_with_text_size_or_font",
        "text_broken",
        "brightness_contrast_too_low",
        "colors_not_distinguishable",
        "colors_barely_distinguishable",
        "continous_color_scale_ambiguos",
        "highlighted_elements_do_not_pop_out",
        "loss_of_detailed_information_content",
        "fabricated_information",
    ];
    const helpful_names = [
        "works_with_one_color",
        "has_labels",
        "descriptive_text",
        "texture_helps",
        "colors_dont_change",
        "order_helps",
    ];

    let ratings = ["Accessible", "Borderline", "Not_Accessible"].reverse();
    let conditions = [
        "Protanomaly",
        "Deuteranomaly",
        "Tritanomaly",
        "Monochromacy",
        //"Overall",
    ];
    let papertrack = ["Vis", "InfoVis", "VAST", "SciVis"];
    let years = ["2000", "2005", "2010", "2015", "2019"].map((d) => +d);

    const all_names = issues_names.concat(helpful_names);

    const names_dict = {
        resolution_too_low: "lores",
        shapes_too_thinsmall: "thin",
        issues_with_text_size_or_font: "textize",
        text_broken: "brotex",
        brightness_contrast_too_low: "locont",
        colors_not_distinguishable: "colnodi",
        colors_barely_distinguishable: "colbadi",
        continous_color_scale_ambiguos: "scambi",
        highlighted_elements_do_not_pop_out: "hinopo",
        loss_of_detailed_information_content: "infloss",
        fabricated_information: "fabinf",
        works_with_one_color: "singcol",
        has_labels: "labels",
        descriptive_text: "desc",
        texture_helps: "texture",
        colors_dont_change: "colstay",
        order_helps: "order",
    };

    const image_type_dict = {
        "2d abstract": "2D abstract",
        "2d continous": "2D continuous",
        "3d abstract": "3D abstract",
        "3d continous": "3D continuous",
        "schema": "schema",
        "gui": "GUI",
        "none": "undetermined"
        
    }

    const margin = 20;

    let data = null;
    let min_max_vote_data = [];

    let ratings_colors = {
        Accessible: "#479fb5",
        Borderline: "#0a5f93",
        Not_Accessible: "#252961",
    };

    $: ratings_color_scale = d3
        .scaleOrdinal()
        .domain(ratings)
        .range(ratings.map((key) => ratings_colors[key]));

    let svg1;

    //console.log(d3)
    let condition_table = [
        {
            name: "Momentan",
            value: {
                //["Deuteranomaly", "Monochromacy", "Protanomaly", "Tritanomaly"]
                Protanomaly: "#9f003e",
                Deuteranomaly: "#74a032",
                Tritanomaly: "#006bf3",
                Monochromacy: "#000000",
                "Overall": "#666666"
            },
        },
    ];
    let condition_colors = condition_table[0].value;

    let svg2;
    let svg2_1;
    let svg2_2;

    $: aspect_table = ((svg) => [
        {
            name: "is",
            value: {
                helpful: (() => {
                    const texture = textures
                        .lines()
                        .orientation("3/8")
                        .size(6)
                        .lighter(1)
                        .stroke("#ffffff");
                    d3.select(svg).call(texture);
                    return texture.url();
                })(),
                issues: (() => {
                    const texture = textures
                        .lines()
                        .orientation("7/8")
                        .size(6)
                        .lighter(1)
                        .stroke("#ffffff");
                    d3.select(svg).call(texture);
                    return texture.url();
                })(),
            },
        },
    ])(svg2);

    $: aspect_textures = aspect_table[0].value;

    $: color_table = [
        {
            name: "Ampel",
            value: {
                Accessible: "#2e8b57",
                Borderline: "#fff281",
                Not_Accessible: "#ff4500",
            },
        },
        {
            name: "Blaue Ampel",
            value: {
                Accessible: "#4682b4",
                Borderline: "#fff281",
                Not_Accessible: "#ff4500",
            },
        },
        {
            name: "Purples",
            value: {
                Accessible: d3.color(d3.interpolatePurples(0.1)).hex(),
                Borderline: d3.color(d3.interpolatePurples(0.5)).hex(),
                Not_Accessible: d3.color(d3.interpolatePurples(1)).hex(),
            },
        },
        {
            name: "Monochrome",
            value: {
                Accessible: d3.color(d3.interpolateGreys(0.1)).hex(),
                Borderline: d3.color(d3.interpolateGreys(0.5)).hex(),
                Not_Accessible: d3.color(d3.interpolateGreys(1)).hex(),
            },
        },
        {
            name: "Blues",
            value: {
                Accessible: "#c0d9f2",
                Borderline: "#6daed5",
                Not_Accessible: "#08306b",
            },
        },
        {
            name: "Textures",
            value: {
                Accessible: ((bg = null) => {
                    const texture = textures
                        .lines()
                        .orientation("2/8")
                        .size(4)
                        .lighter(3)
                        .background("#c0d9f2");
                    d3.select(svg1).call(texture);
                    return texture.url();
                })(),
                Borderline: ((bg = null) => {
                    const texture = textures
                        .lines()
                        .orientation("4/8")
                        .size(4)
                        .lighter(2)
                        .background("#6daed5");
                    d3.select(svg1).call(texture);
                    return texture.url();
                })(),
                Not_Accessible: ((bg = null) => {
                    const texture = textures
                        .lines()
                        .orientation("6/8")
                        .size(4)
                        .lighter(1)
                        .background("#08306b");
                    d3.select(svg1).call(texture);
                    return texture.url();
                })(),
            },
        },
    ];

    let data5 = { height: 500, width: 500, max: -Infinity, agg: [] };
    let issue_dist = { height: 240, width: 900 };

    let image_type_data = []
    let image_type_votes = []
    let image_type_rankings = []

    onMount(async () => {
        aspect_textures = aspect_table[0].value;
        min_max_vote_data = await d3.csv(
            `${window.location.href}data/majority_minority_issues_overview_years_pictures.csv`,
            d3.autoType
        );

        let accessibility_rating_new =  await d3.csv(
            `${window.location.href}data/accessibility_rating_new.csv`,
            d3.autoType
        );

        /** SORRY FOR THE HARDCODING */
        const helpful_order = [4,2,1,5,3,0];
        const issue_order = [4, 6, 5, 7,10,8,2,9,0,1,3];
        const row_order = [0,1,2,3,4,6,5]
        /** SORRY FOR THE HARDCODING */
        image_type_data = (await Promise.all(["/data/helps_by_imgType.csv", "/data/issues_by_imgType.csv"].map(path => {
            return d3.csv(path, d3.autoType)
        }))).map((image_type_data, i) => {
            image_type_data.forEach(row => {
                row.img_type = image_type_dict[row.img_type]
                row.code = names_dict[row.code]
            })
            image_type_data.color = "black"
            image_type_data.cell_size = 80;
            image_type_data.x_padding = 0.25;
            image_type_data.y_padding = 0.15;
            image_type_data.image_types = Array.from(new Set(image_type_data.map((d) => d.img_type)));
            image_type_data.image_types = row_order.map(i => image_type_data.image_types[i])
            image_type_data.code = Array.from(new Set(image_type_data.map((d) => d.code)))
            image_type_data.code = [helpful_order, issue_order][i].map(i => image_type_data.code[i])
            image_type_data.y = d3.scaleBand()
                .domain(image_type_data.image_types)
                .range([0, image_type_data.cell_size / 2.5 * image_type_data.image_types.length])
                .padding(image_type_data.y_padding)
            image_type_data.x = d3.scaleBand()
                .domain(image_type_data.code)
                .range([0, image_type_data.cell_size * image_type_data.code.length])
                .padding(image_type_data.x_padding)
            image_type_data.w = image_type_data.x.bandwidth();
            image_type_data.h = image_type_data.y.bandwidth();
            image_type_data.x_vote = d3.scaleLinear().domain([0, 1]).range([0, image_type_data.w])
            return image_type_data
        });

        image_type_votes = await d3.csv("/data/accessibility_by_imgType.csv", d3.autoType);
        image_type_votes.forEach(row => {
            row.img_type = image_type_dict[row.img_type]
        })
        
        image_type_votes.width = 1000;
        image_type_votes.height = 200;
        image_type_votes.image_types = image_type_data[0].image_types; // image_type_data has two entries
        image_type_votes.x = d3.scaleBand()
            .domain(image_type_votes.image_types)
            .range([0, image_type_votes.width])
            .paddingInner(0.2)
        image_type_votes.y = d3.scaleLinear()
            .domain([0, 1])
            .range([image_type_votes.height, 0])
        image_type_votes.w = image_type_votes.x.bandwidth();
        image_type_votes.x_vote = d3.scaleBand()
            .domain([...ratings].reverse())
            .range([0, image_type_votes.w])
            .paddingInner(0.1);
        image_type_votes.w_vote = image_type_votes.x_vote.bandwidth();


        image_type_rankings = await d3.csv("/data/ranking_by_imgType.csv", d3.autoType);
        image_type_rankings.forEach(row => {
            row.img_type = image_type_dict[row.img_type]
        })
        let last_rank = image_type_rankings.length;
        image_type_rankings = image_type_rankings.filter(d => d.img_type == "2D abstract" || d.img_type == "3D continuous")
        image_type_rankings.color = d3.scaleOrdinal(d3.schemeTableau10).domain(["2D abstract", "3D continuous"]);
        image_type_rankings.last_rank = last_rank
        image_type_rankings.width = 850;
        image_type_rankings.height = 250;
        image_type_rankings.ticks = 30;
        image_type_rankings.x = d3.scaleLinear()
            .domain([0, image_type_rankings.last_rank])
            .rangeRound([0, image_type_rankings.width])
        image_type_rankings.histograms = d3.rollups(image_type_rankings, 
            d => {
                return [...fkde.density1d(d.map(v => v.rank), {
                    bandwidth: 60,
                    pad: 2, 
                    extent: [0, image_type_rankings.last_rank]
                })]
            },
            d => d.img_type
        )
        image_type_rankings.y = d3.scaleLinear()
            .domain([0, d3.max(image_type_rankings.histograms.map(d => d[1]).flat(Infinity), d => d.y)])
            .range([image_type_rankings.height, 0]);
        image_type_rankings.line = d3.line()
            .x(d => image_type_rankings.x(d.x))
            .y(d => image_type_rankings.y(d.y))

        

        console.log("image_type_data", image_type_data)
        console.log("image_type_votes", image_type_votes)
        console.log("image_type_rankings", image_type_rankings)


        let issue_x = d3
            .scaleBand()
            .domain(Array.from(new Set(min_max_vote_data.map((d) => d.label))))
            .range([0, 300]);
        let year_x = d3
            .scaleBand()
            .domain(
                Array.from(new Set(min_max_vote_data.map((d) => "" + d.year)))
            )
            .range([0, 250])
            .paddingInner(0.1);
        min_max_vote_data = d3.groups(
            min_max_vote_data,
            (d) => d.condition,
            (d) => d.label,
            (d) => d.year
        );
        min_max_vote_data = conditions.map(c => min_max_vote_data.find(d => d[0] == c))
        min_max_vote_data.issue_x = issue_x;
        min_max_vote_data.year_x = year_x;
        min_max_vote_data.h = 60
        min_max_vote_data.H = 90
        
        min_max_vote_data.y = d3.scaleLinear().domain([0, 300]).range([min_max_vote_data.h, 0]);
        //console.log("min_max_vote_data", min_max_vote_data);
        //data = await Promise.all(datasets.map(f => d3.csv(`./data/paper/${f}`)))
        let res = await d3.csv(
            `${window.location.href}data/all_data_with_cvd_without_extra-1308.csv`,
            d3.autoType
        );


        min_maj_rating = await d3.csv(
            `${window.location.href}data/accessibility_rating_new.csv`,
            d3.autoType)

        const min_maj_rating_reduce = (images) => {
            //console.log("images", images)
            let minority = ratings.map(key => images.filter(d => d[key] > 0).length / images.length);
            let majority = ratings.map(key => images.filter(d => d.max_vote_conclusive == key).length / images.length);
            return {minority, majority}
        };
        const min_maj_rating_overall = ["Overall", min_maj_rating_reduce(min_maj_rating)]
        min_maj_rating = d3.rollups(min_maj_rating, min_maj_rating_reduce, d => d.condition)
        min_maj_rating.push(min_maj_rating_overall)


        //console.log("min maj accessibility data", min_maj_rating)
        console.log("raw data", res);
        /* ratings = Array.from(new Set(res.map(({accessibility_rating}) => accessibility_rating)));
        conditions = Array.from(new Set(res.map(({condition}) => condition)));
        papertrack = Array.from(new Set(res.map(({paper_type4}) => paper_type4)));
        years = Array.from(new Set(res.map(({year}) => year))); */

        // Average Ratings over the years
        data5.agg = conditions;
        //data5.agg = Array.from(new Set(res.map(({paper_type4}) => paper_type4)))
        //data5.agg = Array.from(new Set(res.map(({condition}) => condition)))
        //data5.acc = d => d["paper_type4"]
        data5.acc = (d) => d["condition"];
        data5.data = d3.rollups(
            res,
            (v) => {
                let result = {};
                ratings.forEach(
                    (key) =>
                        (result[key] = {
                            mean: d3.mean(v, (d) => d[key]),
                            sum: d3.sum(v, (d) => d[key]),
                            count: v.filter((d) => d[key] == 1).length,
                        })
                );
                result.issues = d3.mean(v, (d) => d["#_issues_found"]);
                result.helpful = d3.mean(v, (d) => d["#_helpful_aspects"]);
                data5.max = Math.max(data5.max, result.issues, result.helpful);
                result.count = v.length;
                return result;
            },
            data5.acc,
            (d) => d.year
        );

        const average_data_reduce = (images) => {
            let result = {};
            let lastmean = 0;
            [...ratings].reverse().forEach((key, i) => {
                let mean = d3.mean(images, (d) => d[key]);
                result[key] = {
                    lastmean: lastmean,
                    mean: mean,
                    sum: d3.sum(images, (d) => d[key]),
                    count: images.filter((d) => d[key] > 0).length,
                }
                lastmean += mean;
            });
            result.issues = d3.mean(images, (d) => d["#_issues_found"]);
            result.helpful = d3.mean(images, (d) => d["#_helpful_aspects"]);
            result.count = images.length;
            return result;
        }

        average_data = d3.rollups(
            res, 
            average_data_reduce, 
            d => d.year
        );
        average_data.push(["Overall", average_data_reduce(res)])

        average_data.height = 175;
        average_data.width_ratings = 420;
        average_data.width_aspects = 150;
        average_data.padding = 35;
        average_data.y = d3.scaleBand()
            .domain([...average_data.map(d => d[0]), "Overall"])
            .range([0, average_data.height])
            .paddingInner(0.05)
            .paddingOuter(0.1)
        average_data.x_ratings = d3.scaleLinear()
            .domain([0, 1])
            .range([0, average_data.width_ratings])
        average_data.x_aspects = d3.scaleLinear()
            .domain([0, d3.max(average_data, d => Math.max(d[1].issues, d[1].helpful))])
            .range([0, average_data.width_aspects])

        console.log("average_data", average_data)

        for (const [condition, values] of data5.data) {
            values.push([
                "overall",
                ((v) => {
                    let result = {};
                    ratings.forEach(
                        (key) =>
                            (result[key] = {
                                mean: d3.mean(v, (d) => d[key]),
                                sum: d3.sum(v, (d) => d[key]),
                                count: v.filter((d) => d[key] == 1).length,
                            })
                    );
                    result.issues = d3.mean(v, (d) => d["#_issues_found"]);
                    result.helpful = d3.mean(v, (d) => d["#_helpful_aspects"]);
                    data5.max = Math.max(
                        data5.max,
                        result.issues,
                        result.helpful
                    );

                    result.count = v.length;
                    return result;
                })(res.filter((d) => data5.acc(d) == condition)),
            ]);
        }
        download(data5, "ratings_over_year_per_cvd.json");
        data5.title = "Average Ratings over the years";
        //console.log("data5", data5);

        // Average Label by condition
        issue_dist.agg = conditions;
        issue_dist.acc = (d) => d.condition;
        //issue_dist.acc = d => d.paper_type4
        //issue_dist.agg = Array.from(new Set(res.map(issue_dist.acc)));

        function rollup_function(values) {
            let result = {};
            all_names.forEach(
                (key) =>
                    (result[key] = {
                        mean: d3.mean(values, (d) => d[key]),
                    })
            );
            const images = d3.rollups(
                values,
                (images) => {
                    const result = {};
                    result.count = images.length;
                    result.sums = all_names.map((key) => d3.sum(images, (d) => d[key]));
                    result.ratings = ratings.map(key => d3.sum(images, d => d[key]))
                    return result;
                },
                (d) => d.original_image
            );
            result.ones = {};
            result.halfs = {};
            all_names.forEach((key, i) => (result.ones[key] = images.filter((d) => d[1].sums[i] >= 1).length / images.length));
            all_names.forEach((key, i) => (result.halfs[key] = images.filter((d) => d[1].sums[i] >= d[1].count / 2).length / images.length));
            const R = d3.rollups(
                values,
                (rs) => {
                    const result = {};
                    result.count = rs.length;
                    result.sums = ratings.map((key) => d3.sum(rs, (d) => d[key]));
                    result.majority_sums = ratings.map((key) => d3.sum(rs, (d) => d[key]));
                    if (result.majority_sums[0] == result.majority_sums[2]) {
                        result.majority_sums[1] += result.majority_sums[0]
                        result.majority_sums[1] += result.majority_sums[2]
                        result.majority_sums[0] = 0;
                        result.majority_sums[2] = 0;
                    }

                    let sorted_majority_sums = result.majority_sums.map((d, i) => [i, d]).sort((a, b) => b[1] - a[1]);
                    // if top 2 are the same, and one of them is the borderline rating... move votes to borderline
                    if (sorted_majority_sums[0][1] == sorted_majority_sums[1][1] && (sorted_majority_sums[1][0] == 1 || sorted_majority_sums[0][1] == 1)) {
                        let [rating, votes] = sorted_majority_sums.find(d => d[0] != 1)
                        result.majority_sums[1] += votes;
                        result.majority_sums[rating] -= votes;
                    }
                    return result;
                },
                (d) => d.original_image
            );
            //console.log(R)
            result.ones_rating = {};
            result.halfs_rating = {};
            result.means_rating = {};

            ratings.forEach((key, i) => (result.ones_rating[key] = R.filter((d) => d[1].sums[i] >= 1).length / R.length));
            ratings.forEach((key, i) => (result.halfs_rating[key] = R.filter((d) => d[1].majority_sums[i] >= d[1].count / 2).length / R.length ));
            ratings.forEach((key, i) => (result.means_rating[key] = d3.mean(R, (d) => d[1].sums[i] / d[1].count)));
            //result.images = images;
            return result;
        }
        issue_dist.heigth = 320
        issue_dist.width = 1000
        issue_dist.data = d3.rollups(
            res,
            rollup_function,
            issue_dist.acc
        );

        // add overall
        issue_dist.data.push(["Overall", rollup_function(res)])

        issue_dist.title = "" //"Average Label by condition";
        //console.log("issue_dist", issue_dist);

        download(issue_dist, "labels_per_cvd.json");
        /* contourdata = await d3.csv("./data/paperHighscore2D.csv", d3.autoType, row => {
            row.Accessibility -= 2
            return row;
        }); */
        contourdata.width = 500;
        //console.log("contourdata", contourdata);

        comp.data = d3.rollups(
            await d3.csv(
                `${window.location.href}data/comp.csv`,
                d3.autoType,
                (row) => ((row.Percent /= 100), row)
            ),
            (v) => {
                let res = {};
                v.forEach(
                    ({ Accessibility, Percent }) =>
                        (res[Accessibility] = Percent)
                );
                return res;
            },
            (d) => d.condition,
            (d) => d.simulator
        );

        //console.log("comp", comp);

        data = res;
    });

    function download() {}

    let comp = { height: 150, width: 300 };

    let contourdata = [];
    let contours;
    // Gaussian 2d
    $: sigma2_x = d3.deviation(contourdata, (d) => d["Issues"]);
    $: sigma2_y = d3.deviation(contourdata, (d) => d["Helpful aspects"]);

    $: kernel = (x0, y0, v) => {
        //return (x, y) => v * Math.exp(-(((Math.pow(x - x0, 2) / sigma2_x) + Math.pow(y - y0, 2) / sigma2_y)));
        return (x, y) =>
            Math.sign(v) *
            Math.max(
                0,
                (3 / 4) *
                    (1 -
                        (Math.pow((x - x0) / sigma2_x, 2) +
                            Math.pow((y - y0) / sigma2_y, 2)))
            );
    };

    $: kernels = contourdata.map((d) => {
        return kernel(
            d["Issues"],
            d["Helpful aspects"],
            d["Accessibility"] - 1
        );
    });

    $: value = (x, y) => d3.mean(kernels, (K) => K(x, y));

    let svg4;
    let xc_s = 60;
    let yc_s = 60;
    $: xc = d3
        .scaleLinear()
        .domain([0, d3.max(contourdata, (d) => d["Issues"])])
        .nice()
        .range([0, xc_s]);
    $: xp = d3
        .scaleLinear()
        .domain([0, xc_s])
        .nice()
        .range([0, contourdata.width]);
    $: yc = d3
        .scaleLinear()
        .domain([0, d3.max(contourdata, (d) => d["Helpful aspects"])])
        .nice()
        .range([0, yc_s]);
    $: yp = d3
        .scaleLinear()
        .domain([0, yc_s])
        .nice()
        .range([contourdata.width, 0]);

    function proj(xp, yp) {
        return d3.geoTransform({
            point: function (x, y) {
                this.stream.point(xp(x), yp(y));
            },
        });
    }
    $: drawPath = d3.geoPath(proj(xp, yp));
    //$: grid = Array.from(new druid.Matrix(xc_s, yc_s, (i, j) => kernels.reduce((sum, K, i) => sum + K(xc(i), yc(j)), 0))._data);
    $: grid = Array.from(
        new druid.Matrix(xc_s, yc_s, (i, j) =>
            value(xc.invert(i), yc.invert(j))
        )._data
    );

    //$: console.log("grid", grid);

    //$: vc = d3.scaleSequential(d3.interpolateCividis).domain([0, d3.max(grid, d => d)])
    $: vc = d3.scaleSequential(d3.interpolateBlues).domain([1, -1]);
    //$: vc = d3.scaleLinear().domain([-1, 0, 1]).range([d3.interpolateRdGy(0), "transparent" ,d3.interpolateRdGy(1)])
    $: contours = d3
        .contours()
        .size([xc_s, yc_s])
        .thresholds(vc.ticks(100))(grid)
        .filter(({ value }) => value != 0); //.sort((a, b) => Math.abs(b.value) - Math.abs(a.value) || Math.sign(b.value) ? -1 : 1 )

    //$: console.log("contours", contours);

    $: comp.height = 150;
    $: comp.width = 400;
    $: comp.y = d3.scaleLinear().domain([0, 1]).range([comp.height, 0]);
    $: comp.x1 = d3
        .scaleBand()
        .domain(conditions.slice(0, -1))
        .paddingInner(0.15)
        .range([0, comp.width]);
    $: comp.W = comp.x1.bandwidth();
    $: comp.x2 = d3
        .scaleBand()
        .domain(["initial simulator", "VSS"])
        .paddingInner(0.1)
        .range([0, comp.W]);
    $: comp.w = comp.x2.bandwidth();

    $: data5.height = 150;
    $: data5.x = d3.scaleLinear().domain([0, 1]).range([data5.height, 0]);
    $: data5.width1 = 400;
    $: data5.x1 = d3
        .scaleBand()
        .domain([...data5.agg])
        .paddingInner(0.2)
        .range([0, data5.width1]);
    $: data5.width2 = data5.x1.bandwidth();
    $: data5.x2 = d3
        .scaleBand()
        .domain(years.concat("overall"))
        .paddingInner(0.1)
        .range([0, data5.width2]);

    let svg3;
    $: data5.height3 = Math.ceil(data5.height / 2 - 5);
    $: data5.x3 = d3
        .scaleLinear()
        .domain([data5.max, -data5.max])
        .range([-data5.height3, data5.height3]);
    //$: console.log(data5.x3)

    $: issue_dist.x = d3
        .scaleLinear()
        .domain([1, 0])
        .nice()
        .range([0, issue_dist.height]);
    $: issue_dist.x1 = d3
        .scaleBand()
        .domain(issue_dist.agg ? [...issue_dist.agg] : [])
        .paddingInner(0.1)
        //.paddingOuter(0.1)
        .range([0, issue_dist.width])
        .round(true);

    $: issue_dist.width2 = issue_dist.x1.bandwidth();
    $: issue_dist.x2 = d3
        .scaleBand()
        .domain(all_names.map((_, i) => i))
        .paddingInner(0.2)
        .range([0, issue_dist.width2])
        .round(true);

    $: issue_dist.x_h =  d3
        .scaleBand()
        .domain(helpful_names.map((_, i) => i))
        .paddingInner(0.2)
        .range([0, issue_dist.width2])
        .round(true);
    $: issue_dist.x_i =  d3
        .scaleBand()
        .domain(issues_names.map((_, i) => i))
        .paddingInner(0.2)
        .range([0, issue_dist.width2])
        .round(true);

    $: issue_dist.w = issue_dist.x2.bandwidth();
    $: issue_dist.w_h = issue_dist.x_h.bandwidth();
    $: issue_dist.w_i = issue_dist.x_i.bandwidth();

    let svg6;
    $: issue_dist.r_height = 225;
    $: issue_dist.r_width = 750;
    $: issue_dist.rx = d3
        .scaleBand()
        .domain([...conditions, "Overall"])
        .paddingInner(0.2)
        .range([0, issue_dist.r_width]);
    $: issue_dist.rW = issue_dist.rx.bandwidth();
    $: issue_dist.ry = d3
        .scaleLinear()
        .domain([0, 1])
        .range([issue_dist.r_height, 0]);
    $: issue_dist.rx2 = d3
        .scaleBand()
        .domain(ratings)
        .paddingInner(0.1)
        .range([issue_dist.rW, 0]);
    $: issue_dist.rw = issue_dist.rx2.bandwidth();

    //$: console.log("issue dist 6th", issue_dist);

    function fitToContent(node, data) {
        const svg = d3.select(node);
        const g = svg.select("g").node();
        const fit = () => {
            const { x, y, width, height } = g.getBBox();
            svg
                .attr("width", width + Math.abs(x) + 2 * margin)
                .attr("height", height + Math.abs(y) + 2 * margin);
            d3.select(g).attr("transform", `translate(${margin + Math.abs(x)}, ${margin + Math.abs(y)})`)
        };
        fit();

        return {
            update(data) {
                data5.x.range([data5.height, 0]);
                fit();
            },
        };
    }

    function drawAxis(node, { scale, axis, ticks = null, tickFormat = (d) => d, attrs = {}, domain = true }) {
        d3.select(node).call(
            axis(scale)
                .ticks(ticks)
                .tickSizeOuter(0)
                .tickFormat(tickFormat)
        );
        const tick_text = d3.select(node)
            .selectAll(".tick")
            .select("text")
         /*    .attr("font-size", "16px") */

        if (domain == "remove") {
            d3.select(node).selectAll(".domain").remove()
        }
        for (const [a, v] of Object.entries(attrs)) {
            tick_text.attr(a, v)
        }

        if (attrs.border) {
            tick_text.each(function(d) {
                let {x, y, width} = this.getBBox();
                tick_text.attr("stroke", "white")
                    .attr("stroke-width", "2px")
                    .attr("paint-order", "stroke")
                d3.select(this.parentElement).insert("rect", "text")
                    .attr("x", x + "px")
                    .attr("y", y + (parseInt(attrs["font-size"]) || 16) - 2 + "px")
                    .attr("width", width + "px")
                    .attr("height", (parseInt(attrs["font-size"]) || 16) / 3 + "px")
                    .attr("fill", condition_colors[d])
            })
        }

        return {
            update({ scale, axis }) {
                d3.select(node).call(
                    axis(scale)
                        .ticks(ticks)
                        .tickSizeOuter(0)
                        .tickFormat(tickFormat)
                );
            },
        };
    }

    function drawLegend(node, [scale, title]) {
        const svg = d3.select(node);
        const l = legend
            .legendColor()
            .title(title)
            .titleWidth(100)
            .orient("vertical")
            .cells(15);

        l.scale(scale)(svg);
        svg.selectAll(".legendTitle").attr("font-size", 10).attr("y", 5);
        svg.selectAll(".legendCells").attr("font-size", 10);

        return {
            update([scale, title]) {
                l.title(title).scale(scale)(svg);
                svg.selectAll(".legendTitle").attr("font-size", 10);
                svg.selectAll(".legendCells").attr("font-size", 10);
            },
        };
    }

    function colorBrightness(c) {
        const d3c = d3.color(c);
        if (d3c == null) return 255;
        const { r, g, b } = d3c;
        return (r * 299 + g * 587 + b * 114) / 1000;
    }

    function hideIfTooLowSpace(node, [size, ratings_colors]) {
        const text = d3.select(node).select("text");
        const { width } = node.getBBox();
        const path = d3.select(node.parentNode).select("path");
        text.attr("opacity", width < size ? 1 : 0);
        text.attr(
            "fill",
            colorBrightness(path.attr("fill")) < 130 ? "white" : "black"
        );
        return {
            update([size, ratings_colors]) {
                const { width } = node.getBBox();
                const path = d3.select(node.parentNode).select("path");
                text.attr("opacity", width < size ? 1 : 0);
                text.attr(
                    "fill",
                    colorBrightness(path.attr("fill")) < 130 ? "white" : "black"
                );
            },
        };
    }

    function saveSvg(svgElement, filename) {
        svgElement.setAttribute("xmlns", "http://www.w3.org/2000/svg");
        const svgData = svgElement.outerHTML;
        const preface = '<?xml version="1.0" standalone="no"?>\r\n';
        const svgBlob = new Blob([preface, svgData], {
            type: "image/svg+xml;charset=utf-8",
        });
        const svgUrl = URL.createObjectURL(svgBlob);
        const downloadLink = document.createElement("a");
        downloadLink.href = svgUrl;
        downloadLink.download = filename;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
    }

    let svg5;
    let svg_min_max_vote;

    let min_maj_rating;
    let average_data;
    let svg6_1;

    let svg_average;

    let svg7;
    let svg8 = [];
    let svg9;
</script>

<MaterialApp>
    <slot />
    <Container>
        {#if data == null}
            <Overlay>
                <ProgressCircular color="white" indeterminate size={128} />
            </Overlay>
        {:else}
            <Row class="mb-4">
                <h5>Settings</h5>
            </Row>
            <Row>
                <Select
                    items={color_table}
                    bind:value={ratings_colors}
                    class="ml-2 mt-2"
                >
                    Select color table for ratings
                </Select>
            </Row>
            <Row>
                {#if ratings_colors.Accessible != color_table[5].value.Accessible}
                    {#each ratings as r}
                        <Col cols="2">
                            <input type="color" bind:value={ratings_colors[r]}/>
                            {r}
                        </Col>
                    {/each}
                    <!-- <Col cols="2">
                        {JSON.stringify(ratings_colors)}
                    </Col> -->
                {/if}
            </Row>
            <Row>
                {#each conditions as c}
                    <Col cols="2">
                        <input type="color" bind:value={condition_colors[c]} />
                        {c}
                    </Col>
                {/each}
            </Row>

            <Row>
                <h5>Average Data</h5>
            </Row>
            <Row>
                {#if average_data}
                    <svg bind:this={svg_average} use:fitToContent>
                        <g font-family="Inter">
                            <g id="ratings over the year">
                                <text y=-5 font-weight=900 font-size=20>Average Ratings</text>
                                <g transform="translate(-4, 0)" use:drawAxis={{
                                    scale: average_data.y,
                                    axis: d3.axisLeft,
                                }}/>
                                <g>
                                    {#each average_data as [year, images]}
                                        <g transform="translate(0, {average_data.y(year)})">
                                            {#each ratings as r, i}
                                                <path 
                                                    d="M {average_data.x_ratings(images[r].lastmean)} 0 
                                                        v {average_data.y.bandwidth()} 
                                                        h {average_data.x_ratings(images[r].mean)} 
                                                        v -{average_data.y.bandwidth()} 
                                                        Z"
                                                    fill={ratings_colors[r]} />
                                                <text
                                                    text-anchor="middle"
                                                    dominant-baseline="central"
                                                    fill={i > 2 ? "black" : "white"}
                                                    font-size=16
                                                    font-weight=600
                                                    x={average_data.x_ratings(images[r].lastmean) + average_data.x_ratings(images[r].mean) / 2}
                                                    y={average_data.y.bandwidth() / 2}>
                                                    {d3.format(".0f")(images[r].mean * 100)}%
                                                </text> 
                                            {/each}
                                        </g>
                                    {/each}    
                                </g>
                            </g>
                            <g id="issues over the year"> 
                                <g transform="translate({average_data.x_ratings.range()[1] + 100}, 0)">
                                    <text y=-5 font-weight=900 font-size=20>Issues</text>
                                    <g transform="translate(-4, 0)" use:drawAxis={{
                                        scale: average_data.y,
                                        axis: d3.axisLeft,
                                    }}/>
                                    <g>
                                    {#each average_data as [year, images]}
                                        <g transform="translate(0, {average_data.y(year)})">
                                            <path d="M 0 0 v {average_data.y.bandwidth()} H {average_data.x_aspects(images.issues)} v -{average_data.y.bandwidth()} Z"
                                                fill={condition_colors["Overall"]} />
                                                <text 
                                                    text-anchor="end"
                                                    fill="white"
                                                    font-size=16
                                                    font-weight=600
                                                    dominant-baseline="central"
                                                    y={average_data.y.bandwidth() / 2}
                                                    x={average_data.x_aspects(images.issues) - 12}>
                                                    {d3.format(".2f")(images.issues)}
                                                </text>
                                        </g>
                                    {/each}
                                    </g>
                                </g>
                            </g>
                            <g id="helpful over the year"> 
                                <g transform="translate({average_data.x_ratings.range()[1] + 100 + average_data.x_aspects.range()[1] + 100}, 0)">
                                    <text y=-5 font-weight=900 font-size=20>Helpful</text>
                                    <g transform="translate(-4, 0)" use:drawAxis={{
                                        scale: average_data.y,
                                        axis: d3.axisLeft,
                                    }}/>
                                    <g>
                                        {#each average_data as [year, images]}
                                            <g transform="translate(0, {average_data.y(year)})">
                                                <path d="M 0 0 v {average_data.y.bandwidth()} H {average_data.x_aspects(images.helpful)} v -{average_data.y.bandwidth()} Z"
                                                    fill={condition_colors["Overall"]} />
                                                <text 
                                                    text-anchor="end"
                                                    fill="white"
                                                    font-size=16
                                                    font-weight=600
                                                    dominant-baseline="central"
                                                    y={average_data.y.bandwidth() / 2}
                                                    x={average_data.x_aspects(images.helpful) - 12}>
                                                    {d3.format(".2f")(images.helpful)}
                                                </text>
                                            </g>
                                        {/each}
                                    </g>
                                </g>
                            </g>
                        </g>
                    </svg>
                {/if}
            </Row>
            <Row>
                <Col>
                    <p class="text-right">
                        <Button on:click={function () { saveSvg(svg_average, "averages.svg"); }}>
                            Save
                        </Button>
                    </p>
                </Col>
            </Row>
            <Divider />
            <Row>
                <h5>Minority Majority Votes</h5>
            </Row>
            <Row>
                <svg  bind:this={svg_min_max_vote} use:fitToContent>
                    <defs>
                        {#each [...conditions, "Overall"] as c}
                            <pattern id="hatch_{c}" x="0" y="0" width="5.345059133378628" height="5.345059133378628" patternUnits="userSpaceOnUse">
                                <path style="stroke:{condition_colors[c]};stroke-width:0.25mm" d="M -3.7795275590551185,3.7795275590551185 3.7795275590551185,-3.7795275590551185" />
                                <path style="stroke:{condition_colors[c]};stroke-width:0.25mm" d="M -3.7795275590551185,9.124586692433747 9.124586692433747,-3.7795275590551185" />
                                <path style="stroke:{condition_colors[c]};stroke-width:0.25mm" d="M 1.5655315743235096,9.124586692433747 9.124586692433747,1.5655315743235096" />
                            </pattern>
                        {/each}
                    </defs>
                    <g>
                        {#each min_max_vote_data as [condition, issue_vote_data], i}
                            <!-- {condition} -->
                            <g transform="translate({300 * i + (i >= 2 ? 100 : 0)}, 0)">
                                <text
                                    x={0 + min_max_vote_data.year_x.range()[1] / 2}
                                    y=25
                                    font-size="24"
                                    font-weight="900"
                                    font-family="Inter"
                                    text-anchor="middle">
                                    {condition}
                                </text>
                                <g
                                    transform="translate(0, {95 + (issue_vote_data.length - 1) * min_max_vote_data.H + 30})"
                                    use:drawAxis={{
                                        scale: min_max_vote_data.year_x,
                                        axis: d3.axisBottom,
                                    }}
                                >
                                <!--  {#each min_max_vote_data.year_x.domain() as year, i}
                                        <path
                                            d="M {min_max_vote_data.year_x(year) +
                                                min_max_vote_data.year_x.bandwidth() /
                                                    2} 0 v -{issue_vote_data.length *
                                                45}"
                                            fill="none"
                                            stroke="#ddd"
                                        />
                                    {/each} -->
                                </g>
                                {#each issue_vote_data as [issue, year_vote_data], j}
                                    <g transform="translate(0, {95 + j * min_max_vote_data.H})">
                                        {#if i == 0}
                                            <text
                                                x="625"
                                                y="-20"
                                                text-anchor="middle"
                                                font-size=20
                                                dominant-baseline="central">
                                                {names_dict[issue.replaceAll(" ", "_")]}
                                            </text>
                                            <!-- <path
                                                d="M 0 0 h {min_max_vote_data.length * 300}"
                                                stroke="#ddd"
                                                fill="none"
                                            /> -->
                                        {/if}
                                        <g transform="translate(0, -40)">
                                            {#if i == 3 && j == 0}
                                                <g
                                                    transform="translate(251, 0)"
                                                    use:drawAxis={{
                                                        scale: min_max_vote_data.y,
                                                        axis: d3.axisRight,
                                                        ticks: 3,
                                                    }}
                                                />
                                            {/if}
                                            {#each min_max_vote_data.y.ticks(3) as tick}
                                                <path shape-rendering="crispEdges" 
                                                    d="M -1 {min_max_vote_data.y(tick)} H {min_max_vote_data.year_x.range()[1] + 1}" 
                                                    fill="none" 
                                                    stroke={(tick == 0) ? "#222" : "#bbb"} />
                                            {/each}
                                            {#each year_vote_data as [year, vote_data], k}
                                                <path
                                                    d="M {min_max_vote_data.year_x('' + year)} {min_max_vote_data.y(vote_data[0].minority)} 
                                                    h {min_max_vote_data.year_x.bandwidth()} 
                                                    V {min_max_vote_data.y(0)} 
                                                    h -{min_max_vote_data.year_x.bandwidth()} 
                                                    Z"
                                                    fill="url(#hatch_{condition})"
                                                />
                                                <path
                                                    d="M {min_max_vote_data.year_x('' + year)} {min_max_vote_data.y(vote_data[0].minority)} h {min_max_vote_data.year_x.bandwidth()} "
                                                    fill="none"
                                                    stroke={condition_colors[condition]}
                                                />
                                                <path
                                                    d="M {min_max_vote_data.year_x('' + year)} {min_max_vote_data.y(vote_data[0].majority)} 
                                                    h {min_max_vote_data.year_x.bandwidth()} 
                                                    V {min_max_vote_data.y(0)} 
                                                    h -{min_max_vote_data.year_x.bandwidth()} 
                                                    Z"
                                                    fill={condition_colors[condition]}
                                                />
                                            {/each}

                                            <!-- <path d="M {year_vote_data.map(([year, vote_data] , k) => [min_max_vote_data.year_x(""+year) + min_max_vote_data.year_x.bandwidth() / 2, min_max_vote_data.y(vote_data[0].minority)]).join("L")} V {min_max_vote_data.y(0)} H {min_max_vote_data.year_x.bandwidth() / 2} Z"
                                            fill="url(#hatch_{condition})"></path>
                                        <path d="M {year_vote_data.map(([year, vote_data] , k) => [min_max_vote_data.year_x(""+year) + min_max_vote_data.year_x.bandwidth() / 2, min_max_vote_data.y(vote_data[0].minority)]).join("L")}"
                                            fill="none" stroke={condition_colors[condition]}></path>
                
                                        <path d="M {year_vote_data.map(([year, vote_data] , k) => [min_max_vote_data.year_x(""+year) + min_max_vote_data.year_x.bandwidth() / 2, min_max_vote_data.y(vote_data[0].majority)]).join("L")} V {min_max_vote_data.y(0)} H {min_max_vote_data.year_x.bandwidth() / 2} Z"
                                                fill={condition_colors[condition]} ></path> -->
                                        </g>
                                    </g>
                                {/each}
                            </g>
                        {/each}
                    </g>
                </svg>
            </Row>
            <Row>
                <Col class="text-right">
                    <Button on:click={function () {saveSvg(svg_min_max_vote, "min_max_vote.svg");}}>
                        Save
                    </Button>
                </Col>
            </Row>

            <Divider />
            <Row>
                <h5>Minority Majority Ratings</h5>
            </Row>
            <Row>

                <svg use:fitToContent
                    bind:this={svg6_1}>
                    <defs>
                        {#each ratings as r}
                            <pattern id="hatch_{r}" x="0" y="0" width="5.345059133378628"height="5.345059133378628" patternUnits="userSpaceOnUse">
                                <path style="stroke:{ratings_colors[r]};stroke-width:0.25mm" d="M -3.7795275590551185,3.7795275590551185 3.7795275590551185,-3.7795275590551185"/>
                                <path style="stroke:{ratings_colors[r]};stroke-width:0.25mm" d="M -3.7795275590551185,9.124586692433747 9.124586692433747,-3.7795275590551185"/>
                                <path style="stroke:{ratings_colors[r]};stroke-width:0.25mm" d="M 1.5655315743235096,9.124586692433747 9.124586692433747,1.5655315743235096"/>
                            </pattern>
                        {/each}
                    </defs>
                    <g>
                        <g transform="translate({2 * margin}, {2 * margin})">
                            <g transform="translate(0, -4)"
                                use:drawAxis={{
                                    scale: issue_dist.rx,
                                    axis: d3.axisTop,
                                    attrs: {"font-family": "Inter", "font-weight": 900, "font-size": 22, "dy": -10, border: conditions}
                                }}/>

                            <g transform="translate(-4, 0)"
                                use:drawAxis={{
                                    scale: issue_dist.ry,
                                    ticks: 5,
                                    axis: d3.axisLeft,
                                    tickFormat: d3.format(".0%"),
                                    attrs: {"font-family": "Inter"}
                                }}/>

                            {#each issue_dist.ry.ticks(5) as t}
                                <path
                                    d="M 0 {issue_dist.ry(t) +
                                        0.25} h {issue_dist.r_width}"
                                    fill="none"
                                    stroke="lightgrey"
                                    stroke-width="0.5"
                                />
                            {/each}

                            {#each min_maj_rating as [condition, values]}
                                <g transform="translate({issue_dist.rx(condition)}, 0)">
                                    {#each ratings as r, i}
                                        <g transform="translate({issue_dist.rx2(r)}, 0)">
                                            <!-- minority -->
                                            <path d="M 0 {Math.round((1 - values.minority[i]) * issue_dist.r_height) + .5} h {issue_dist.rw} V {issue_dist.r_height} h {-issue_dist.rw} Z" fill="url(#hatch_{r})" />
                                            <path d="M 0 {Math.round((1 - values.minority[i]) * issue_dist.r_height) + .5} h {issue_dist.rw}" fill="none" stroke={ratings_colors[r]} />
                                            <!-- majority -->
                                            <path d="M 0 {Math.round((1 - values.majority[i]) * issue_dist.r_height) + .5} h {issue_dist.rw} V {issue_dist.r_height} h {-issue_dist.rw} Z" fill={ratings_colors[r]} />
                                            <!-- labels -->
                                            <g transform="translate({issue_dist.rw / 2}, {issue_dist.r_height + 9})"
                                                font-family="Inter" font-size="16px" font-weight=400>
                                                <text dominant-baseline="hanging">
                                                    {String(r).replace("_", " ")[0]}
                                                </text>
                                            </g>
                                        </g>


                                    {/each}
                                </g>
                            {/each}
                    </g>
                </svg>
            </Row>
            <Row>
                <Col class="text-right">
                    <Button on:click={function () { saveSvg(svg6_1, "min_maj_ratings.svg"); }}>
                        Save
                    </Button>
                </Col>
            </Row>

            <Row>
                <h5>Simulator Comparison</h5>
            </Row>
            <Row>
                <svg
                    use:fitToContent={comp}
                    bind:this={svg5}
                    font-family="Inter">
                    <g transform="translate({margin + 20}, {margin + 4})">
                        <g transform="translate(0, -4)" use:drawAxis={{ 
                            scale: comp.x1, 
                            axis: d3.axisTop,  
                            attrs: {"font-family": "Inter", "font-weight": 900, "font-size": 18, "dy": -10}
                            }}/>
                        <g transform="translate(-4, 0)" use:drawAxis={{ 
                            scale: comp.y, 
                            ticks: 5, 
                            axis: d3.axisLeft, 
                            tickFormat: d3.format(".0%"), 
                            "font-family": "Inter", 
                            "font-weight": 200,
                            "font-size": 12
                            }}/>
                        {#each comp.y.ticks(5) as t}
                            <path
                                d="M 0 {comp.y(t) + 0.25} h {comp.width}"
                                fill="none"
                                stroke="lightgrey"
                                stroke-width="0.5"
                            />
                        {/each}
                        {#each comp.data as [condition, values]}
                            <g transform="translate({comp.x1(condition)}, 0)">
                                {#each values as [simulator, ranks]}
                                    <g transform="translate({comp.x2(simulator)}, 0)">
                                        {#each [...ratings].reverse() as r, i}
                                            <path
                                                d="M 0 {comp.height - d3.sum([...ratings].reverse().slice(0, i), (r) => ranks[r]) * comp.height} h {comp.w} v -{ranks[r] * comp.height} h {-comp.w} Z"
                                                fill={ratings_colors[r]}
                                            />
                                        {/each}
                                        <g transform="translate({comp.w / 2}, {comp.height + 9})">
                                            <text
                                                text-anchor="middle"
                                                dominant-baseline="hanging">
                                                {
                                                    {  "initial simulator": "IS",
                                                        VSS: "VSS",}[simulator]
                                                }
                                            </text>
                                        </g>
                                    </g>
                                {/each}
                            </g>
                        {/each}
                    </g>
                </svg>
            </Row>
            <Row class="float-right">
                <Button
                    on:click={function () {
                        saveSvg(svg5, "simulator_comparison.svg");
                    }}>Save</Button
                >
            </Row>

            <Row>
                <h5>ImageType</h5>
            </Row>
            <Row>
                {#each image_type_data as image_type_data, i}
                <svg
                    use:fitToContent={comp}
                    bind:this={svg8[i]}
                    font-family="Inter">
                    <defs>
                            <pattern id="hatch_image_type" x="0" y="0" width="5.345059133378628" height="5.345059133378628" patternUnits="userSpaceOnUse">
                                <path style="stroke: {image_type_data.color}; stroke-width:0.25mm" d="M -3.7795275590551185,3.7795275590551185 3.7795275590551185,-3.7795275590551185"/>
                                <path style="stroke: {image_type_data.color}; stroke-width:0.25mm" d="M -3.7795275590551185,9.124586692433747 9.124586692433747,-3.7795275590551185"/>
                                <path style="stroke: {image_type_data.color}; stroke-width:0.25mm" d="M 1.5655315743235096,9.124586692433747 9.124586692433747,1.5655315743235096"/>
                            </pattern>
                    </defs>
                    <g transform="translate(20, 20)">

                        <g>
                            {#each image_type_data.code as code, i}
                                <g transform="translate({image_type_data.x(code)}, 0)">
                                    {#each image_type_data.x_vote.ticks(5) as tick} 
                                        <path d="M {image_type_data.x_vote(tick)} 0 v {image_type_data.y.range()[1]}" 
                                            fill="none" 
                                            stroke="#ddd" />
                                    {/each}
                                    <path 
                                        d="M {image_type_data.x_vote(0)} 0 v {image_type_data.y.range()[1]}" 
                                        fill="none"
                                        stroke="#000"/>
                                    <path 
                                        d="M {image_type_data.x_vote(1)} 0 v {image_type_data.y.range()[1]}" 
                                        fill="none"
                                        stroke="#bbb"/>
                                </g>
                            {/each}
                            <g transform="translate({image_type_data.x(image_type_data.code[image_type_data.code.length - 1])}, {image_type_data.y.range()[1]})" 
                                use:drawAxis={{
                                    scale: image_type_data.x_vote, 
                                    axis: d3.axisBottom,
                                    ticks: 3,
                                    tickFormat: d3.format(".0%"),
                                    attrs: {"font-familty": "Inter", "font-size": 12}
                                }} />
                        </g>
                        <g use:drawAxis={{ 
                            scale: image_type_data.y, 
                            axis: d3.axisLeft,  
                            attrs: {"font-family": "Inter", "font-weight": 600, "font-size": 15},
                            domain: "remove",
                            }} />
                        <g use:drawAxis={{ 
                            scale: image_type_data.x, 
                            axis: d3.axisTop,  
                            attrs: {"font-family": "Inter", "font-weight": 600, "font-size": 15},
                            domain: "remove",
                            }}  />
                        <g>
                            {#each image_type_data as row, i}
                                <g transform="translate({image_type_data.x(row.code)}, {image_type_data.y(row.img_type)})">
                                    <!-- <rect width={image_type_data.w} height={image_type_data.h} fill="none" stroke="#ddd"  shape-rendering="crispEdges" ></rect> -->
                                    
                                    <rect width={image_type_data.x_vote(row.minority_relative)} height={image_type_data.h} fill="url(#hatch_image_type)" stroke="none" />
                                    <path d="M {image_type_data.x_vote(row.minority_relative)} 0 v {image_type_data.h}" fill="none" stroke={image_type_data.color} />
                                    <rect width={image_type_data.x_vote(row.majority_relative)} height={image_type_data.h} fill={image_type_data.color} stroke="none" />
                                </g>
                            {/each}
                        </g>  
                    </g>
                </svg>
                {/each}
            </Row>
            <Row class="float-right">

                {#each image_type_data as image_type_data, i}
                <Button
                    on:click={function () {
                        saveSvg(svg8[i], `image_type_${["helpful", "issue"][i]}_vis.svg`);
                    }}>Save</Button
                >
                {/each}
            </Row>

            <Row>
                <svg
                    use:fitToContent={comp}
                    bind:this={svg7}>
                    <defs>
                        {#each ratings as r}
                            <pattern id="hatch_{r}" x="0" y="0" width="5.345059133378628" height="5.345059133378628" patternUnits="userSpaceOnUse">
                                <path style="stroke: {ratings_color_scale(r)}; stroke-width:0.25mm" d="M -3.7795275590551185,3.7795275590551185 3.7795275590551185,-3.7795275590551185"/>
                                <path style="stroke: {ratings_color_scale(r)}; stroke-width:0.25mm" d="M -3.7795275590551185,9.124586692433747 9.124586692433747,-3.7795275590551185"/>
                                <path style="stroke: {ratings_color_scale(r)}; stroke-width:0.25mm" d="M 1.5655315743235096,9.124586692433747 9.124586692433747,1.5655315743235096"/>
                            </pattern>
                        {/each}
                    </defs>
                    <g transform="translate(20, 20)">
                        <g>
                            <!-- {#each image_type_data.code as code, i}
                                <g transform="translate({image_type_data.x(code)}, 0)">
                                    {#each image_type_data.x_vote.ticks(5) as tick} 
                                        <path d="M {image_type_data.x_vote(tick)} 0 v {image_type_data.y.range()[1]}" 
                                            fill="none" 
                                            stroke="#ddd" />
                                    {/each}
                                    <path 
                                        d="M {image_type_data.x_vote(0)} 0 v {image_type_data.y.range()[1]}" 
                                        fill="none"
                                        stroke="#000"/>
                                    <path 
                                        d="M {image_type_data.x_vote(1)} 0 v {image_type_data.y.range()[1]}" 
                                        fill="none"
                                        stroke="#bbb"/>
                                </g>
                            {/each}
                            <g transform="translate({image_type_data.x(image_type_data.code[image_type_data.code.length - 1])}, {image_type_data.y.range()[1]})" 
                                use:drawAxis={{
                                    scale: image_type_data.x_vote, 
                                    axis: d3.axisBottom,
                                    ticks: 3,
                                    tickFormat: d3.format(".0%"),
                                    attrs: {"font-familty": "Inter", "font-size": 12}
                                }} /> -->
                        </g>
                        <g use:drawAxis={{ 
                            scale: image_type_votes.y, 
                            axis: d3.axisLeft,  
                            tickFormat: d3.format(".0%"),
                            ticks: 5,
                            }} >
                            {#each image_type_votes.y.ticks(5) as tick}
                                <path 
                                    d="M 0 {image_type_votes.y(tick)} h {image_type_votes.width}"
                                    fill="none"
                                    stroke="#ddd" />
                            {/each}
                        </g>
                        
                        <g use:drawAxis={{ 
                            scale: image_type_votes.x, 
                            axis: d3.axisTop,  
                            attrs: {"font-family": "Inter", "font-weight": 600, "font-size": 15},
                            domain: "remove",
                            }}  />
                        <g>
                           <!--  content here -->
                           {#each image_type_votes as d}
                                <g transform="translate({image_type_votes.x(d.img_type)}, 0)">
                                    <g transform="translate({image_type_votes.x_vote(d.rating)}, {image_type_votes.height})">
                                        <path 
                                            d="M 0 0 h {image_type_votes.w_vote} V -{image_type_votes.y(1-d.minority_relative)} h -{image_type_votes.w_vote}"
                                            fill="url(#hatch_{d.rating})"
                                            stroke="none"/>
                                        <path 
                                            d="M 0 -{image_type_votes.y(1-d.minority_relative)} h {image_type_votes.w_vote} "
                                            fill="none"
                                            stroke={ratings_color_scale(d.rating)} />
                                        <path 
                                            d="M 0 0 h {image_type_votes.w_vote} v -{image_type_votes.y(1-d.majority_relative)} h -{image_type_votes.w_vote}"
                                            fill={ratings_color_scale(d.rating)} 
                                            stroke="none" />
                                    </g>
                                </g>
                           {/each}
                        </g>  
                    </g>
                </svg>
            </Row>
            <Row class="float-right">
                <Button
                    on:click={function () {
                        saveSvg(svg7, "image_type_rating_vis.svg");
                    }}>Save</Button
                >
            </Row>

                <Row>
                    <svg
                        use:fitToContent={comp}
                        bind:this={svg9}>
                        <g transform="translate(20, 20)">
                            <!-- <text y="-20"
                                font-size="24"
                                font-weight="900"
                                font-family="Inter">
                                {img_type}
                            </text> -->
                            <g use:drawAxis={{
                                axis: d3.axisLeft, 
                                scale: image_type_rankings.y, 
                                ticks: 5,
                                attrs: {"font-family": "Inter", "font-size": "1.2em"}
                                }} >
                                {#each image_type_rankings.y.ticks(5) as tick}
                                    <path d="M 0 {image_type_rankings.y(tick)} h {image_type_rankings.width}"
                                        fill="none"
                                        stroke="#ddd"
                                        />
                                {/each}
                            </g>

                            <g transform="translate(0, {image_type_rankings.height})"
                                use:drawAxis={{
                                    axis: d3.axisBottom, 
                                    scale: image_type_rankings.x,
                                    ticks: 15,
                                    attrs: {"font-family": "Inter", "font-size": "1.2em"}
                                    }} >
                                <text
                                    fill="black"
                                    font-family="Inter"
                                    font-size=18
                                    dominant-baseline="hanging" 
                                    x={image_type_rankings.width}
                                    y=28>
                                    rank →
                                </text>
                            </g>

                            {#each image_type_rankings.histograms as [img_type, kde]}
                                <g stroke-width=2>
                                    <path 
                                        d={image_type_rankings.line(kde)}
                                        stroke={image_type_rankings.color(img_type)}
                                        fill="none" 
                                        stroke-dasharray={img_type == "2D abstract" ? [4, 2] : null}
                                        opacity={img_type == "2D abstract" || img_type == "3D continuous" ? 1: 0.2}
                                        /> 
                                </g>
                                <g>
                                    <text 
                                        font-family="Inter"
                                        font-size=18
                                        font-weight=600
                                        dominant-baseline="central"
                                        x={image_type_rankings.x(image_type_rankings.last_rank) + 12} 
                                        y={image_type_rankings.y(kde[kde.length - 1].y)}>
                                        {img_type}
                                    </text>
                                </g>
                            {/each}
                            
                        </g>
                    </svg>
                </Row>
                <Row class="float-right">
                    <Button
                        on:click={function () {
                            saveSvg(svg9, "image_type_ranking_vis.svg");
                        }}>Save</Button
                    >
                </Row>
            <!-- <Row>
                <h5>First Vis</h5>
            </Row>
            <Row>
                <Col cols="3">
                    Height
                    <input
                        type="range"
                        min={100}
                        max={600}
                        bind:value={data5.height}
                        step={1}
                    />
                    <span>
                        <TextField bind:value={data5.height} />
                    </span>
                </Col>
                <Col cols="3">
                    Width
                    <input
                        type="range"
                        min={100}
                        max={600}
                        bind:value={data5.width1}
                        step={1}
                    />
                    <span slot="append-outer">
                        <TextField bind:value={data5.width1} />
                    </span>
                </Col>
                <Col cols="3">
                    <Switch bind:checked={data5.showPercentages}
                        >Show numbers</Switch
                    >
                </Col>
            </Row>
            <Row>
                <svg
                    use:fitToContent={data5}
                    bind:this={svg1}
                    font-family="Inter"
                >
                    <g transform="translate({40 + margin}, {20 + margin})">
                        <g transform="translate({2 * margin}, {margin})">
                            
                            <g
                                transform="translate(-4, 0)"
                                use:drawAxis={{
                                    scale: data5.x,
                                    ticks: 5,
                                    axis: d3.axisLeft,
                                    tickFormat: d3.format(".0%"),
                                }}
                            />
                            <g
                                transform="translate(0, -4)"
                                use:drawAxis={{
                                    scale: data5.x1,
                                    axis: d3.axisTop,
                                }}
                            />
                            {#each data5.x.ticks(5) as t}
                                <path
                                    d="M 0 {data5.x(t) + 0.25} h {data5.width1 +
                                        5}"
                                    fill="none"
                                    stroke="lightgrey"
                                    stroke-width="0.5"
                                />
                            {/each}
                            {#each data5.data as [condition, values]}
                                <g
                                    transform="translate({data5.x1(
                                        condition
                                    )}, 0)"
                                >
                                    {#each values as [year, values]}
                                        <g
                                            transform="translate({data5.x2(
                                                year
                                            ) +
                                                (year == 'overall'
                                                    ? 5
                                                    : 0)}, 0)"
                                        >
                                            <g
                                                transform="translate({data5.x2.bandwidth() /
                                                    2}, {data5.height +
                                                    9}) rotate(60)"
                                            >
                                                <text
                                                    font-size="10"
                                                    font-family="sans-serif"
                                                    dominant-baseline="central"
                                                    >{year == "overall"
                                                        ? "overall"
                                                        : String(year).substr(
                                                              2
                                                          )}</text
                                                >
                                            </g>
                                            {#each ratings as r, i}
                                                <g>
                                                    <path
                                                        d="M 0 {(1 -
                                                            d3.sum(
                                                                ratings.slice(
                                                                    0,
                                                                    i
                                                                ),
                                                                (key) =>
                                                                    values[key]
                                                                        .mean
                                                            )) *
                                                            data5.height}
                                                        h {data5.x2.bandwidth()}
                                                        v {-data5.height *
                                                            values[r].mean} 
                                                        h {-data5.x2.bandwidth()} 
                                                        Z"
                                                        fill={ratings_colors[r]}
                                                    >
                                                        <title
                                                            >{values[r]
                                                                .mean}</title
                                                        >
                                                    </path>
                                                    {#if data5.showPercentages == true}
                                                        <g
                                                            use:hideIfTooLowSpace={[
                                                                data5.height *
                                                                    values[r]
                                                                        .mean,
                                                                ratings_colors,
                                                            ]}
                                                            transform="translate({data5.x2.bandwidth() /
                                                                2}, {(1 -
                                                                d3.sum(
                                                                    ratings.slice(
                                                                        0,
                                                                        i
                                                                    ),
                                                                    (key) =>
                                                                        values[
                                                                            key
                                                                        ].mean
                                                                )) *
                                                                data5.height -
                                                                (data5.height *
                                                                    values[r]
                                                                        .mean) /
                                                                    2}) rotate(-90)"
                                                        >
                                                            <text
                                                                bind:this={values[
                                                                    r
                                                                ].textElement}
                                                                text-anchor="middle"
                                                                dominant-baseline="central"
                                                                font-size="10"
                                                            >
                                                                {d3.format(
                                                                    ".1f"
                                                                )(
                                                                    values[r]
                                                                        .mean *
                                                                        100
                                                                )}
                                                            </text>
                                                        </g>
                                                    {/if}
                                                </g>
                                            {/each}
                                        </g>
                                    {/each}
                                </g>
                            {/each}
                        </g>
                    </g>
                </svg>
            </Row>
            <Row class="float-right">
                <Button
                    on:click={function () {
                        saveSvg(
                            svg1,
                            `FirstVis_${
                                color_table.find(
                                    (t) =>
                                        t.value.Accessible ==
                                        ratings_colors.Accessible
                                ).name
                            }.svg`
                        );
                    }}>Save</Button
                >
            </Row> -->
            <Divider />
            <Row>
                <h5>Minority Majority Ratings - Overall</h5>
            </Row>
            <!-- <Row>
                <Col cols="3">
                    Height
                    <input
                        type="range"
                        min={100}
                        max={600}
                        bind:value={issue_dist.height}
                        step={1}
                    />
                    <span slot="append-outer">
                        <TextField bind:value={issue_dist.height} />
                    </span>
                </Col>
                <Col cols="3">
                    Width
                    <input
                        type="range"
                        min={100}
                        max={1600}
                        bind:value={issue_dist.width}
                        step={1}
                    />

                    <span slot="append-outer">
                        <TextField bind:value={issue_dist.width} />
                    </span>
                </Col>
            </Row> -->
            <!--<Row>
                 <svg
                    use:fitToContent={issue_dist}
                    bind:this={svg2}
                    font-family="Inter">
                    <defs>
                        {#each conditions as c}
                            <pattern
                                id="hatch_{c}"
                                inkscape:stockid="Hatch 1.0 x 45°"
                                x="0"
                                y="0"
                                width="5.345059133378628"
                                height="5.345059133378628"
                                patternUnits="userSpaceOnUse"
                            >
                                <path
                                    style="stroke:{condition_colors[
                                        c
                                    ]};stroke-width:0.25mm"
                                    d="M -3.7795275590551185,3.7795275590551185 3.7795275590551185,-3.7795275590551185"
                                />
                                <path
                                    style="stroke:{condition_colors[
                                        c
                                    ]};stroke-width:0.25mm"
                                    d="M -3.7795275590551185,9.124586692433747 9.124586692433747,-3.7795275590551185"
                                />
                                <path
                                    style="stroke:{condition_colors[
                                        c
                                    ]};stroke-width:0.25mm"
                                    d="M 1.5655315743235096,9.124586692433747 9.124586692433747,1.5655315743235096"
                                />
                            </pattern>
                        {/each}
                    </defs>
                    <g transform="translate({margin}, {margin})">
                        <g transform="translate(20, 40)"
                            use:drawAxis={{
                                scale: issue_dist.x,
                                axis: d3.axisLeft,
                                tickFormat: d3.format(".0%"),
                                ticks: 5,
                            }}/>
                        <g transform="translate(20, 35)"
                            use:drawAxis={{
                                scale: issue_dist.x1,
                                axis: d3.axisTop,
                                attrs: {"font-family": "Inter", "font-weight": 900, "font-size": 20, "dy": -10, "border": conditions}
                            }}/>
                        {#each issue_dist.x.ticks(5) as t}
                            <path
                                d="M 0 {issue_dist.x(t) +
                                    0.25} h {issue_dist.width + 5}"
                                fill="none"
                                stroke="lightgrey"
                                stroke-width="0.5"
                            />
                        {/each}
                        <g transform="translate(20, 40)">
                            {#each issue_dist.data.slice(0, 4) as [condition, values]}
                                <g transform="translate({issue_dist.x1(condition)}, 0)">
                                    {#each helpful_names
                                        .sort((a, b) => values[b].mean - values[a].mean)
                                        .concat(issues_names.sort((a, b) => values[b].mean - values[a].mean)) as key, i}
                                        <g transform="translate({issue_dist.x2(i) +(issues_names.includes(key) ? 4 : 0)}, 0)">
                                            <g transform="translate({issue_dist.x2.bandwidth() / 2}, {9 + issue_dist.height}) rotate(60)">
                                                <text
                                                    font-size="10"
                                                    dominant-baseline="central">
                                                    <tspan
                                                        font-family="monospace"
                                                        font-weight="bold">
                                                        {helpful_names.includes(key) ? "+" : "–"}
                                                    </tspan>
                                                    {names_dict[key]}
                                                </text>
                                            </g>
                                            {#if all_names.includes(key)}
                                                <g transform="translate({issue_dist.x2.bandwidth() / 2}, 0)">
                                                    <path
                                                        d="M 0 {issue_dist.height - issue_dist.x(issue_dist.x.domain()[0] - values.ones[key])}
                                                           h {issue_dist.w} 
                                                           V {issue_dist.height} 
                                                           h {-issue_dist.w} 
                                                           Z"
                                                        fill="url(#hatch_{condition})"
                                                    />
                                                    <path
                                                        d="M 0 {issue_dist.height - issue_dist.x(issue_dist.x.domain()[0] - values.ones[key])}
                                                           h {issue_dist.w}"
                                                        fill="none"
                                                        stroke={condition_colors[condition]}
                                                    />
                                                    <path
                                                        d="M 0 {issue_dist.height - issue_dist.x(issue_dist.x.domain()[0] - values.halfs[key])} 
                                                           h {issue_dist.w} 
                                                           V {issue_dist.height} 
                                                           h {-issue_dist.w} 
                                                           Z"
                                                        fill={condition_colors[condition]}
                                                    />
                                                </g>
                                            {/if}
                                        </g>
                                    {/each}
                                </g>
                            {/each}
                        </g>
                    </g>
                </svg>
            </Row>--> 
            <Row>
                <svg
                    use:fitToContent={issue_dist}
                    bind:this={svg2_1}
                    font-family="Inter">
                    <defs>
                        {#each conditions as c}
                            <pattern id="hatch_{c}" x="0" y="0" width="5.345059133378628" height="5.345059133378628" patternUnits="userSpaceOnUse">
                                <path style="stroke:{condition_colors[c]}; stroke-width:0.25mm" d="M -3.7795275590551185,3.7795275590551185 3.7795275590551185,-3.7795275590551185"/>
                                <path style="stroke:{condition_colors[c]}; stroke-width:0.25mm" d="M -3.7795275590551185,9.124586692433747 9.124586692433747,-3.7795275590551185"/>
                                <path style="stroke:{condition_colors[c]}; stroke-width:0.25mm" d="M 1.5655315743235096,9.124586692433747 9.124586692433747,1.5655315743235096"/>
                            </pattern>
                        {/each}
                    </defs>
                    <g transform="translate({margin}, {margin})">
                        <g transform="translate(20, 40)"
                            use:drawAxis={{
                                scale: issue_dist.x,
                                axis: d3.axisLeft,
                                tickFormat: d3.format(".0%"),
                                ticks: 5,
                            }}/>
                        <g transform="translate(20, 35)"
                            use:drawAxis={{
                                scale: issue_dist.x1,
                                axis: d3.axisTop,
                                attrs: {"font-family": "Inter", "font-weight": 900, "font-size": "20px", "dy": -10, "border": conditions}
                            }}/>
                        <g transform="translate(20, 40)">
                            {#each issue_dist.x.ticks(5) as t}
                                <path
                                    d="M 0 {issue_dist.x(t) +
                                        0.25} h {issue_dist.width + 5}"
                                    fill="none"
                                    stroke="lightgrey"
                                    stroke-width="0.5"
                                />
                            {/each}
                            {#each issue_dist.data.slice(0, 4) as [condition, values]}
                                <g transform="translate({issue_dist.x1(condition)}, 0)">
                                    <!-- {#if condition == "Overall"}
                                        <rect width={issue_dist.x1.bandwidth()} height={issue_dist.height} fill="black" />
                                    {/if} -->
                                    {#each issues_names.sort((a, b) => d3.ascending(a, b)) as key, i}
                                        <g transform="translate({issue_dist.x_i(i)}, 0)">
                                            <g transform="translate({issue_dist.x_i.step() - 5}, {9 + issue_dist.height}) rotate(60)">
                                                <text
                                                    font-size="16"
                                                    dominant-baseline="central">
                                                    <tspan
                                                        font-family="monospace"
                                                        font-weight="bold">
                                                        {helpful_names.includes(key) ? "+" : "–"}
                                                    </tspan>
                                                    {names_dict[key]}
                                                </text>
                                            </g>
                                            {#if all_names.includes(key)}
                                                <g transform="translate({issue_dist.x_i.bandwidth() / 2}, 0)">
                                                    <path
                                                        d="M 0 {issue_dist.height - issue_dist.x(issue_dist.x.domain()[0] - values.ones[key])}
                                                           h {issue_dist.w_i} 
                                                           V {issue_dist.height} 
                                                           h {-issue_dist.w_i} 
                                                           Z"
                                                        fill="url(#hatch_{condition})"
                                                    />
                                                    <path
                                                        d="M 0 {issue_dist.height - issue_dist.x(issue_dist.x.domain()[0] - values.ones[key])}
                                                           h {issue_dist.w_i}"
                                                        fill="none"
                                                        stroke={condition_colors[condition]}
                                                    />
                                                    <path
                                                        d="M 0 {issue_dist.height - issue_dist.x(issue_dist.x.domain()[0] - values.halfs[key])} 
                                                           h {issue_dist.w_i} 
                                                           V {issue_dist.height} 
                                                           h {-issue_dist.w_i} 
                                                           Z"
                                                        fill={condition_colors[condition]}
                                                    />
                                                </g>
                                            {/if}
                                        </g>
                                    {/each}
                                </g>
                            {/each}
                        </g>
                    </g>
                </svg>
            </Row><Row>
                <svg
                    use:fitToContent={issue_dist}
                    bind:this={svg2_2}
                    font-family="Inter">
                    <defs>
                        {#each conditions as c}
                            <pattern
                                id="hatch_{c}"
                                inkscape:stockid="Hatch 1.0 x 45°"
                                x="0"
                                y="0"
                                width="5.345059133378628"
                                height="5.345059133378628"
                                patternUnits="userSpaceOnUse"
                            >
                                <path
                                    style="stroke:{condition_colors[
                                        c
                                    ]};stroke-width:0.25mm"
                                    d="M -3.7795275590551185,3.7795275590551185 3.7795275590551185,-3.7795275590551185"
                                />
                                <path
                                    style="stroke:{condition_colors[
                                        c
                                    ]};stroke-width:0.25mm"
                                    d="M -3.7795275590551185,9.124586692433747 9.124586692433747,-3.7795275590551185"
                                />
                                <path
                                    style="stroke:{condition_colors[
                                        c
                                    ]};stroke-width:0.25mm"
                                    d="M 1.5655315743235096,9.124586692433747 9.124586692433747,1.5655315743235096"
                                />
                            </pattern>
                        {/each}
                    </defs>
                    <g transform="translate({margin}, {margin})">
                        <text font-size="14">{issue_dist.title}</text>
                        <g
                            transform="translate(20, 40)"
                            use:drawAxis={{
                                scale: issue_dist.x,
                                axis: d3.axisLeft,
                                tickFormat: d3.format(".0%"),
                                ticks: 5,
                            }}/>
                        <g
                            transform="translate(20, 35)"
                            use:drawAxis={{
                                scale: issue_dist.x1,
                                axis: d3.axisTop,
                                attrs: {"font-family": "Inter", "font-weight": 900, "font-size": 20, "dy": -10, "border": conditions}
                            }}/>
                        <g transform="translate(20, 40)">
                            {#each issue_dist.x.ticks(5) as t}
                                <path
                                    d="M 0 {issue_dist.x(t) +
                                        0.25} h {issue_dist.width + 5}"
                                    fill="none"
                                    stroke="lightgrey"
                                    stroke-width="0.5"
                                />
                            {/each}
                            {#each issue_dist.data.slice(0, 4) as [condition, values]}
                                <g transform="translate({issue_dist.x1(condition)}, 0)">
                                    <!-- {#if condition == "Overall"}
                                        <rect width={issue_dist.x1.bandwidth()} height={issue_dist.height} fill="black" />
                                    {/if} -->
                                    {#each helpful_names.sort((a, b) => d3.ascending(a, b)) as key, i}
                                        <g transform="translate({issue_dist.x_h(i)}, 0)">
                                            <g transform="translate({issue_dist.x_h.step() - 8}, {9 + issue_dist.height}) rotate(60)">
                                                <text
                                                    font-size="16"
                                                    dominant-baseline="central">
                                                    <tspan
                                                        font-family="monospace"
                                                        font-weight="bold">
                                                        {helpful_names.includes(key) ? "+" : "–"}
                                                    </tspan>
                                                    {names_dict[key]}
                                                </text>
                                            </g>
                                            {#if all_names.includes(key)}
                                                <g transform="translate({issue_dist.x_h.bandwidth() / 2}, 0)">
                                                    <path
                                                        d="M 0 {issue_dist.height - issue_dist.x(issue_dist.x.domain()[0] - values.ones[key])}
                                                           h {issue_dist.w_h} 
                                                           V {issue_dist.height} 
                                                           h {-issue_dist.w_h} 
                                                           Z"
                                                        fill="url(#hatch_{condition})"
                                                    />
                                                    <path
                                                        d="M 0 {issue_dist.height - issue_dist.x(issue_dist.x.domain()[0] - values.ones[key])}
                                                           h {issue_dist.w_h}"
                                                        fill="none"
                                                        stroke={condition_colors[condition]}
                                                    />
                                                    <path
                                                        d="M 0 {issue_dist.height - issue_dist.x(issue_dist.x.domain()[0] - values.halfs[key])} 
                                                           h {issue_dist.w_h} 
                                                           V {issue_dist.height} 
                                                           h {-issue_dist.w_h} 
                                                           Z"
                                                        fill={condition_colors[condition]}
                                                    />
                                                </g>
                                            {/if}
                                        </g>
                                    {/each}
                                </g>
                            {/each}
                        </g>
                    </g>
                </svg>
            </Row>
            
            <Row>
                <Col class="text-right">
                    <!-- <Button on:click={function () {saveSvg(svg2, "min_max_rating_all.svg");}}>
                        Save (all)
                    </Button> -->
                    <Button on:click={function () {saveSvg(svg2_1, "min_maj_rating_issues.svg");}}>
                        Save (issues)
                    </Button>
                    <Button on:click={function () {saveSvg(svg2_2, "min_maj_rating_helpful.svg");}}>
                        Save (helpful)
                    </Button>
                </Col>
            </Row>

            <!-- <Row>
                <h5>Third Vis</h5>
            </Row>
            <Row>
                <svg
                    use:fitToContent={data5}
                    bind:this={svg3}
                    font-family="sans-serif"
                >
                    <g transform="translate({40 + margin}, {20 + margin})">
                        <g transform="translate(0, 0)">
                            <g
                                transform="translate(0, -4)"
                                use:drawAxis={{
                                    scale: data5.x1,
                                    axis: d3.axisTop,
                                }}
                            />
                            <g
                                transform="translate(-4, {data5.height / 2})"
                                use:drawAxis={{
                                    scale: data5.x3,
                                    ticks: 5,
                                    axis: d3.axisLeft,
                                    tickFormat: (d) => Math.abs(d),
                                }}
                            >
                                {#each data5.x3.ticks(5) as t}
                                    <path
                                        d="M 0 {data5.x3(t) +
                                            0.25} h {data5.width1 + 4 + 5}"
                                        fill="none"
                                        stroke="lightgrey"
                                        stroke-width="0.5"
                                    />
                                {/each}
                            </g>
                            <g transform="translate(-4, {data5.height / 2})">
                                <g transform="translate({-24}, -{data5.height / 4}) rotate(-90)">
                                    <text dominant-baseline="central"
                                        text-anchor="middle"
                                        font-size="10">
                                        <tspan font-family="monospace" font-weight="bold">+</tspan> helpful
                                    </text>
                                </g>
                                <g
                                    transform="translate({-24}, {data5.height /
                                        4}) rotate(-90)"
                                >
                                    <text
                                        dominant-baseline="central"
                                        text-anchor="middle"
                                        font-size="10"
                                        ><tspan
                                            font-family="monospace"
                                            font-weight="bold">–</tspan
                                        > issues</text
                                    >
                                </g>
                            </g>

                            {#each data5.data as [condition, values]}
                                <g
                                    transform="translate({data5.x1(
                                        condition
                                    )}, 0)"
                                >
                                    {#each values as [year, values]}
                                        <g
                                            transform="translate({data5.x2(
                                                year
                                            ) +
                                                (year == 'overall'
                                                    ? 5
                                                    : 0)}, 0)"
                                        >
                                            <g
                                                transform="translate(0, {data5.height /
                                                    2})"
                                            >
                                                <path
                                                    d="M 0 0 h {data5.x2.bandwidth()} v {data5.x3(
                                                        values.helpful
                                                    )} h {-data5.x2.bandwidth()}"
                                                    fill={condition_colors[
                                                        condition
                                                    ]}
                                                >
                                                    <title
                                                        >helpful {values.helpful}</title
                                                    >
                                                </path>
                                                <path
                                                    d="M 0 0 h {data5.x2.bandwidth()} v {-data5.x3(
                                                        values.issues
                                                    )} h {-data5.x2.bandwidth()}"
                                                    fill={condition_colors[
                                                        condition
                                                    ]}
                                                >
                                                    <title
                                                        >issues {values.issues}</title
                                                    >
                                                </path>
                                            </g>
                                            <g
                                                transform="translate({data5.x2.bandwidth() /
                                                    2}, {data5.height +
                                                    9}) rotate(60)"
                                            >
                                                <text
                                                    font-size="10"
                                                    font-family="sans-serif"
                                                    dominant-baseline="central"
                                                    >{year == "overall"
                                                        ? "overall"
                                                        : String(year).substr(
                                                              2
                                                          )}</text
                                                >
                                            </g>
                                        </g>
                                    {/each}
                                </g>
                            {/each}
                            <g transform="translate(0, {data5.height / 2})">
                                <path
                                    d="M -4 0.5 h {data5.width1 + 4 + 5}"
                                    fill="none"
                                    stroke="lightgrey"
                                />
                            </g>
                        </g>
                    </g>
                </svg>
            </Row>
            <Row class="float-right">
                <Button
                    on:click={function () {
                        saveSvg(svg3, "ThirdVis.svg");
                    }}>Save</Button
                >
            </Row>-->
            <!-- <Row>
                <h5>Fourth Vis</h5>
            </Row>
            <Row>
                <svg use:fitToContent={data5} bind:this={svg4} font-family="sans-serif">
                    <g transform="translate({margin + 20}, {margin})">
                        <g transform="">
                            {#each contours as contour}
                                <path d={drawPath(contour)} fill={vc(contour.value)} stroke="white" stroke-width=1>
                                    <title>{contour.value}</title>
                                </path>
                            {/each}
                            
                        </g>
                        {#each contourdata as point}
                            <circle cx={xp(xc(point["Issues"])) + Math.random() * 20} cy={yp(yc(point["Helpful aspects"]))+ Math.random() * 25} r=2  fill={vc(point["Accessibility"])}>
                                {point["Accessibility"]}
                            </circle>
                        {/each}
                        <g transform="translate(0, {contourdata.width})" use:drawAxis={{scale: xp, axis: d3.axisBottom, tickFormat: d => d3.format(".2f")(xc.invert(d))}} />
                        <g use:drawAxis={{scale: yp, axis: d3.axisLeft, tickFormat: d => d3.format(".2f")(yc.invert(d))}} />
                        <g transform="translate({contourdata.width + margin}, 0)" use:drawLegend={[vc, "Accessibility"]} />
                    </g>
                </svg>
            </Row>
            <Row class="float-right">
                <Button on:click={function() {saveSvg(svg4, "FourthVis.svg")}}>Save</Button>
            </Row> -->
        {/if}
    </Container>
</MaterialApp>

